import React, { useState, useRef, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import { FiCalendar, FiClock } from 'react-icons/fi';
import { Button } from 'rebass';
import classNames from 'classnames';
import { getDisplayFormat } from 'utils/dateFormats';

/**
 * Converts a Date object into segmented fields for the input
 * @param {Date|null} date - Date to convert
 * @param {boolean} showDate - Whether to populate date fields
 * @param {boolean} showTime - Whether to populate time fields
 * @returns {Object} Segmented date object with month, day, year, hour, minute, ampm
 */
const getDateObject = (date, showDate, showTime) => {
    const dateObject = {
        month: '',
        day: '',
        year: '',
        hour: '',
        minute: '',
        ampm: 'AM'
    };

    if (date) {
        const m = moment(date);
        if (m.isValid()) {
            if (showDate) {
                dateObject.month = String(m.month() + 1).padStart(2, '0');
                dateObject.day = String(m.date()).padStart(2, '0');
                dateObject.year = String(m.year());
            }

            if (showTime) {
                const hours24 = m.hours();
                const hours12 = hours24 % 12 || 12;
                dateObject.hour = String(hours12).padStart(2, '0');
                dateObject.minute = String(m.minutes()).padStart(2, '0');
                dateObject.ampm = hours24 >= 12 ? 'PM' : 'AM';
            }
        }
    }

    return dateObject;
};

/**
 * @typedef {Object} DateSegmentProps
 * @property {string} value - Current value
 * @property {string} placeholder - Placeholder text
 * @property {number} maxLength - Max character length
 * @property {number} max - Maximum allowed value
 * @property {number} [min=1] - Minimum allowed value
 * @property {(value: string, shouldAutoAdvance?: boolean) => void} onValueChange - Callback when value changes
 * @property {(e: FocusEvent) => void} [onFocus] - Focus event handler
 * @property {(e: FocusEvent) => void} [onBlur] - Blur event handler
 * @property {(e: KeyboardEvent) => void} [onKeyDown] - Keydown event handler
 * @property {Object} inputRef - Ref for the input element
 * @property {string} ariaLabel - Accessibility label
 */

/**
 * Single numeric input segment for date/time (month, day, year, hour, minute)
 * @param {DateSegmentProps} props - Component props
 * @returns {JSX.Element} Input element
 */
const DateSegment = ({
    value,
    placeholder,
    maxLength,
    max,
    min = 1,
    onValueChange,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef,
    ariaLabel
}) => {
    const handleChange = e => {
        const newValue = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
        const oldValue = String(value || '');

        // Always allow deletion or clearing (when newValue is shorter than current value or empty)
        if (newValue.length < oldValue.length || newValue === '') {
            onValueChange(newValue);
            return;
        }

        // Only validate when adding characters
        if (newValue.length <= maxLength) {
            const num = parseInt(newValue, 10);
            // Allow partial input, but if complete (maxLength), check against min and max
            if (newValue.length < maxLength || (num >= min && num <= max)) {
                onValueChange(newValue);
            }
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            const num = parseInt(value, 10);
            const newVal = isNaN(num) || value === '' ? min : num >= max ? min : num + 1; // We want to rotate the value when reaching max
            onValueChange(String(newVal).padStart(maxLength, '0'), false);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            const num = parseInt(value, 10);
            const newVal = isNaN(num) || value === '' ? max : num <= min ? max : num - 1; // Here exactly the same as above
            onValueChange(String(newVal).padStart(maxLength, '0'), false);
        } else if (e.key === 'Backspace') {
            // Let backspace work normally to delete characters
            // Only call custom handler if field is empty (to navigate back)
            if (!value && onKeyDown) {
                onKeyDown(e);
            }
            // Otherwise, let the default behavior delete the character
        } else if (onKeyDown) {
            onKeyDown(e);
        }
    };

    return (
        <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            autoComplete="off"
            className="border-0 outline-0 text-center p-0 bg-transparent font-inherit"
            style={{
                width: `${maxLength * 1.6}ch`
            }}
        />
    );
};

/**
 * @typedef {Object} AmPmSegmentProps
 * @property {string} value - Current value ('AM' or 'PM')
 * @property {(value: string) => void} onValueChange - Callback when value changes
 * @property {(e: FocusEvent) => void} [onFocus] - Focus event handler
 * @property {(e: FocusEvent) => void} [onBlur] - Blur event handler
 * @property {(e: KeyboardEvent) => void} [onKeyDown] - Keydown event handler
 * @property {Object} inputRef - Ref for the input element
 * @property {string} ariaLabel - Accessibility label
 */

/**
 * AM/PM toggle input segment
 * @param {AmPmSegmentProps} props - Component props
 * @returns {JSX.Element} Input element
 */
const AmPmSegment = ({ value, onValueChange, onFocus, onBlur, onKeyDown, inputRef, ariaLabel }) => {
    const handleKeyDown = e => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            onValueChange(value === 'AM' ? 'PM' : 'AM');
        } else if (e.key === 'a' || e.key === 'A') {
            e.preventDefault();
            onValueChange('AM');
        } else if (e.key === 'p' || e.key === 'P') {
            e.preventDefault();
            onValueChange('PM');
        } else if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const handleChange = e => {
        // Prevent default onChange behavior - value is controlled via onClick and keyboard events
        // We added it to prevent from an error message in the console
        e.preventDefault();
    };

    return (
        <input
            ref={inputRef}
            type="text"
            value={value || 'AM'}
            onChange={handleChange}
            onClick={() => onValueChange(value === 'AM' ? 'PM' : 'AM')}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            className="border-0 outline-0 text-center p-0 bg-transparent font-inherit"
            style={{
                width: `3.2ch`
            }}
        />
    );
};

/**
 * @typedef {Object} DateInputProps
 * @property {Date|null} value - Current date value
 * @property {(date: Date|null) => void} onChange - Callback when date changes
 * @property {string} [ariaLabelledby] - ID of element that labels this input
 * @property {boolean} [showTimeSelect] - Whether to show time selection with date
 * @property {boolean} [showTimeSelectOnly] - Whether to show only time selection
 * @property {string} [name] - Name attribute for hidden form input
 * @property {Object} [inputRef] - Ref for hidden form input
 */

/**
 * Segmented date/time input with calendar picker and hidden form field
 * @param {DateInputProps} props - Component props
 * @returns {JSX.Element} Segmented date input component with calendar
 */
export const DateInput = ({ value, onChange, ariaLabelledby, showTimeSelect, showTimeSelectOnly, name, inputRef }) => {
    const showDate = !showTimeSelectOnly;
    const showTime = showTimeSelect || showTimeSelectOnly;
    const monthRef = useRef(null);
    const dayRef = useRef(null);
    const yearRef = useRef(null);
    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const ampmRef = useRef(null);
    const [dateTime, setDateTime] = useState(() => getDateObject(value, showDate, showTime));
    const [focusedSegment, setFocusedSegment] = useState(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    /**
     * Converts 12-hour time to 24-hour format
     * @param {string} hour - Hour in 12-hour format
     * @param {string} minute - Minute value
     * @param {string} ap - AM/PM indicator
     * @returns {Object|null} Object with hours and minutes in 24-hour format, or null if invalid
     */
    const parseTime = (hour, minute, ap) => {
        if (!hour || !minute) return null;

        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);

        if (hourNum < 1 || hourNum > 12 || minuteNum < 0 || minuteNum > 59) {
            return null;
        }

        let hours24 = hourNum % 12;
        if (ap === 'PM') hours24 += 12;

        return { hours: hours24, minutes: minuteNum };
    };

    /**
     * Builds a Date object from segmented date/time values
     * @param {Object} dateTime - Segmented date object with month, day, year, hour, minute, ampm
     * @returns {Date|null} Date object if valid, null otherwise
     */
    const buildDate = dateTime => {
        const { month, day, year, hour, minute, ampm } = dateTime;

        if (showTimeSelectOnly) {
            const time = parseTime(hour, minute, ampm);
            if (!time) return null;

            return moment()
                .hours(time.hours)
                .minutes(time.minutes)
                .seconds(0)
                .toDate();
        }

        // Date or Date+Time mode
        if (!month || !day || !year) return null;

        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);

        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || yearNum <= 0) {
            return null;
        }

        const dateObj = { year: yearNum, month: monthNum - 1, date: dayNum };

        if (showTime) {
            const time = parseTime(hour, minute, ampm);

            // If time is required but not provided, don't build the date
            if (!time) {
                return null;
            }

            dateObj.hours = time.hours;
            dateObj.minutes = time.minutes;
        }

        const date = moment(dateObj);
        return date.isValid() ? date.toDate() : null;
    };

    /**
     * Notifies parent component of date changes
     * @param {Object} dateTime - Segmented date object
     */
    const notifyChange = dateTime => {
        const date = buildDate(dateTime);
        onChange(date);
    };

    /**
     * Handles month value changes with optional auto-advance to next field
     * @param {string} newValue - New month value
     * @param {boolean} [shouldAutoAdvance=true] - Whether to auto-advance to next field when complete
     */
    const handleMonthChange = (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, month: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === 2) {
            dayRef.current?.focus();
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles day value changes with optional auto-advance to next field
     * @param {string} newValue - New day value
     * @param {boolean} [shouldAutoAdvance=true] - Whether to auto-advance to next field when complete
     */
    const handleDayChange = (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, day: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === 2) {
            yearRef.current?.focus();
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles year value changes with optional auto-advance to next field
     * @param {string} newValue - New year value
     * @param {boolean} [shouldAutoAdvance=true] - Whether to auto-advance to next field when complete
     */
    const handleYearChange = (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, year: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === 4 && showTime) {
            hourRef.current?.focus();
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles hour value changes with optional auto-advance to next field
     * @param {string} newValue - New hour value
     * @param {boolean} [shouldAutoAdvance=true] - Whether to auto-advance to next field when complete
     */
    const handleHourChange = (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, hour: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === 2) {
            minuteRef.current?.focus();
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles minute value changes with optional auto-advance to next field
     * @param {string} newValue - New minute value
     * @param {boolean} [shouldAutoAdvance=true] - Whether to auto-advance to next field when complete
     */
    const handleMinuteChange = (newValue, shouldAutoAdvance = true) => {
        const newDateTime = { ...dateTime, minute: newValue };
        setDateTime(newDateTime);
        if (shouldAutoAdvance && newValue.length === 2) {
            ampmRef.current?.focus();
        }
        notifyChange(newDateTime);
    };

    /**
     * Handles AM/PM value changes
     * @param {string} newValue - New AM/PM value
     */
    const handleAmpmChange = newValue => {
        const newDateTime = { ...dateTime, ampm: newValue };
        setDateTime(newDateTime);
        notifyChange(newDateTime);
    };

    /**
     * Handles keyboard navigation for month field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleMonthKeyDown = e => {
        if (e.key === 'ArrowRight' || (e.key === '/' && dateTime.month)) {
            e.preventDefault();
            dayRef.current?.focus();
        }
    };

    /**
     * Handles keyboard navigation for day field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleDayKeyDown = e => {
        if (e.key === 'ArrowRight' || (e.key === '/' && dateTime.day)) {
            e.preventDefault();
            yearRef.current?.focus();
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            monthRef.current?.focus();
        } else if (e.key === 'Backspace' && !dateTime.day) {
            e.preventDefault();
            monthRef.current?.focus();
        }
    };

    /**
     * Handles keyboard navigation for year field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleYearKeyDown = e => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            dayRef.current?.focus();
        } else if (e.key === 'Backspace' && !dateTime.year) {
            e.preventDefault();
            dayRef.current?.focus();
        } else if (e.key === 'ArrowRight' && showTime) {
            e.preventDefault();
            hourRef.current?.focus();
        }
    };

    /**
     * Handles keyboard navigation for hour field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleHourKeyDown = e => {
        if (e.key === 'ArrowRight' || (e.key === ':' && dateTime.hour)) {
            e.preventDefault();
            minuteRef.current?.focus();
        } else if (e.key === 'ArrowLeft' && showDate) {
            e.preventDefault();
            yearRef.current?.focus();
        } else if (e.key === 'Backspace' && !dateTime.hour && showDate) {
            e.preventDefault();
            yearRef.current?.focus();
        }
    };

    /**
     * Handles keyboard navigation for minute field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleMinuteKeyDown = e => {
        if (e.key === 'ArrowRight' || (e.key === ' ' && dateTime.minute)) {
            e.preventDefault();
            ampmRef.current?.focus();
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            hourRef.current?.focus();
        } else if (e.key === 'Backspace' && !dateTime.minute) {
            e.preventDefault();
            hourRef.current?.focus();
        }
    };

    /**
     * Handles keyboard navigation for AM/PM field
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleAmpmKeyDown = e => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            minuteRef.current?.focus();
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            minuteRef.current?.focus();
        }
    };

    /**
     * Handles clicks on the input container to focus the first segment
     */
    const handleContainerClick = () => {
        if (!focusedSegment) {
            if (showDate) {
                monthRef.current?.focus();
            } else {
                hourRef.current?.focus();
            }
        }
    };

    /**
     * Toggles the calendar picker visibility
     */
    const handleCalendarIconClick = () => {
        setIsCalendarOpen(prev => !prev);
    };

    /**
     * Handles date selection from the calendar picker
     * @param {Date} date - Selected date from calendar
     */
    const handleCalendarChange = date => {
        const newDateTime = getDateObject(date, showDate, showTime);
        setDateTime(newDateTime);
        notifyChange(newDateTime);
        setIsCalendarOpen(false);
    };

    /**
     * Closes the calendar picker when clicking outside
     */
    const handleClickOutside = () => {
        setIsCalendarOpen(false);
    };

    /**
     * Handles month changes from the calendar dropdown
     * @param {Date} date - Date representing the new month selection
     */
    const handlePickerMonthChange = date => {
        const newDateTime = {
            ...dateTime,
            month: String(date.getMonth() + 1).padStart(2, '0')
        };
        setDateTime(newDateTime);
        notifyChange(newDateTime);
    };

    /**
     * Handles year changes from the calendar dropdown
     * @param {Date} date - Date representing the new year selection
     */
    const handlePickerYearChange = date => {
        const newDateTime = {
            ...dateTime,
            year: String(date.getFullYear())
        };
        setDateTime(newDateTime);
        notifyChange(newDateTime);
    };

    /**
     * Listens for ESC key to close the calendar when open
     */
    useEffect(() => {
        const handleEscKey = event => {
            if (event.key === 'Escape' && isCalendarOpen) {
                setIsCalendarOpen(false);
            }
        };

        if (isCalendarOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isCalendarOpen]);

    // Calculate formatted value for hidden input
    const formattedValue = value ? moment(value).format(getDisplayFormat(showTimeSelectOnly, showTimeSelect)) : '';

    return (
        <>
            <input type="hidden" name={name} ref={inputRef} value={formattedValue} />
            <div
                className={classNames(
                    'relative flex items-center px-3 py-2 h-8 ring-1 ring-shiva rounded hover:ring-bluest-blue w-min',
                    focusedSegment && 'ring-perfect-blue hover:ring-perfect-blue !ring-2'
                )}
                onClick={handleContainerClick}
                aria-labelledby={ariaLabelledby}
            >
                {showDate && (
                    <>
                        <DateSegment
                            inputRef={monthRef}
                            value={dateTime.month}
                            placeholder="MM"
                            maxLength={2}
                            max={12}
                            onValueChange={handleMonthChange}
                            onFocus={() => setFocusedSegment('month')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleMonthKeyDown}
                            ariaLabel="Month"
                        />
                        <span className="text-secondary mx-1">/</span>
                        <DateSegment
                            inputRef={dayRef}
                            value={dateTime.day}
                            placeholder="DD"
                            maxLength={2}
                            max={31}
                            onValueChange={handleDayChange}
                            onFocus={() => setFocusedSegment('day')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleDayKeyDown}
                            ariaLabel="Day"
                        />
                        <span className="text-secondary mx-1">/</span>
                        <DateSegment
                            inputRef={yearRef}
                            value={dateTime.year}
                            placeholder="YYYY"
                            maxLength={4}
                            max={9999}
                            onValueChange={handleYearChange}
                            onFocus={() => setFocusedSegment('year')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleYearKeyDown}
                            ariaLabel="Year"
                        />
                    </>
                )}

                {showDate && showTime && <span className="mx-1 text-secondary"> </span>}

                {showTime && (
                    <>
                        <DateSegment
                            inputRef={hourRef}
                            value={dateTime.hour}
                            placeholder="HH"
                            maxLength={2}
                            max={12}
                            min={1}
                            onValueChange={handleHourChange}
                            onFocus={() => setFocusedSegment('hour')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleHourKeyDown}
                            ariaLabel="Hour"
                        />
                        <span className="text-secondary">:</span>
                        <DateSegment
                            inputRef={minuteRef}
                            value={dateTime.minute}
                            placeholder="mm"
                            maxLength={2}
                            max={59}
                            min={0}
                            onValueChange={handleMinuteChange}
                            onFocus={() => setFocusedSegment('minute')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleMinuteKeyDown}
                            ariaLabel="Minute"
                        />
                        <span className="mx-1 text-secondary"> </span>
                        <AmPmSegment
                            inputRef={ampmRef}
                            value={dateTime.ampm}
                            onValueChange={handleAmpmChange}
                            onFocus={() => setFocusedSegment('ampm')}
                            onBlur={() => setFocusedSegment(null)}
                            onKeyDown={handleAmpmKeyDown}
                            ariaLabel="AM/PM"
                        />
                    </>
                )}
                <span className="mx-1" />
                <Button
                    type="button"
                    variant="transparent-icon"
                    onClick={e => {
                        e.stopPropagation();
                        handleCalendarIconClick();
                    }}
                    className="!p-1 !ml-auto !h-6"
                    aria-label={showTimeSelectOnly ? 'Open time picker' : 'Open calendar'}
                >
                    {showTimeSelectOnly ? <FiClock className="!m-0" /> : <FiCalendar className="!m-0" />}
                </Button>
            </div>
            {isCalendarOpen && (
                <div className="absolute z-1000 top-full left-0 mt-1">
                    <ReactDatePicker
                        selected={value}
                        onChange={handleCalendarChange}
                        onClickOutside={handleClickOutside}
                        onMonthChange={handlePickerMonthChange}
                        onYearChange={handlePickerYearChange}
                        inline
                        todayButton={!showTimeSelectOnly ? 'Today' : undefined}
                        showYearDropdown={!showTimeSelectOnly}
                        showMonthDropdown={!showTimeSelectOnly}
                        dropdownMode="select"
                        showTimeSelect={showTimeSelect || showTimeSelectOnly}
                        showTimeSelectOnly={showTimeSelectOnly}
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat={getDisplayFormat(showTimeSelectOnly, showTimeSelect)}
                    />
                </div>
            )}
        </>
    );
};
